import React, { useEffect, useState } from 'react';
import {
  Container,
  Dropdown,
  Image,
  Nav,
  NavDropdown,
  Navbar
} from 'react-bootstrap';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/validointilogo.png';
import { useAuth } from '../contexts/AuthContext.js';
import { useLocalization } from '../contexts/LocalizationContext.js';
import { useConfirm } from '../hooks/useConfirm.js';
import useModal from '../hooks/useModal.js';
import ChangePasswordModal from './modals/ChangePasswordModal.jsx';

const NavigationBar = () => {
  const modal = useModal();
  const location = useLocation();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const {
    user,
    logout,
    selectedOrganization,
    changeSessionOrganization,
    hasRole,
    getUser
  } = useAuth();
  const { localize, locales, setLocale, selectedLocale } = useLocalization();
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    confirm({
      header: localize('common.logout'),
      message: localize('common.logout.confirm'),
      variant: 'primary',
      okValue: localize('common.yes'),
      onConfirm: async () => {
        await logout();
        navigate('/login', { replace: true });
      }
    });
  };

  const handleOrganizationChange = (id) => {
    confirm({
      header: localize('organization.change'),
      message: localize('organization.change.confim'),
      variant: 'primary',
      okValue: localize('common.yes'),
      onConfirm: async () => {
        await changeSessionOrganization(id);
        await getUser();
        navigate('/');
      }
    });
  };

  const navigationLinks = {
    navigation: [
      {
        label: 'link.users',
        path: '/users',
        permissions: ['user.admin']
      },
      {
        label: 'ruleGroups',
        path: '/rulegroups',
        permissions: ['rule.admin']
      },
      {
        label: 'link.systemSettings',
        submenu: [
          { label: 'link.organizations', path: '/admin/organisations' },
          { label: 'link.users', path: '/admin/users' }
        ],
        permissions: ['root']
      }
    ],
    user: [
      {
        label: 'user.change.password',
        onclick: () => modal.open({ content: <ChangePasswordModal /> })
      },
      {
        label: 'common.logout',
        onclick: handleLogout
      }
    ],
    lang: [
      {
        label: 'link.lang',
        submenu: locales || []
      }
    ]
  };

  return (
    <>
      <Navbar
        bg='dark'
        variant='dark'
        expand='md'
        className='shadow-sm'
        expanded={isExpanded}>
        <Container>
          {/* Logo */}
          <Navbar.Brand
            as={NavLink}
            to='/'
            className='d-flex align-items-center'>
            <Image
              src={logo}
              alt='Logo'
              width='auto'
              height='50'
              className='me-2'
            />
          </Navbar.Brand>

          {/* common links */}
          <Navbar.Toggle
            aria-controls='basic-navbar-nav'
            onClick={() => setIsExpanded((prev) => !prev)}
          />
          <Navbar.Collapse id='basic-navbar-nav'>
            <Nav className='me-auto'>
              {user &&
                navigationLinks?.navigation.map((item, index) => {
                  return (
                    (!item.permissions || hasRole(...item.permissions)) &&
                    (item.submenu ? (
                      <NavDropdown
                        key={index}
                        title={localize(item.label)}
                        id={`nav-dropdown-${index}`}>
                        {item.submenu.map((subitem, subindex) => (
                          <NavDropdown.Item
                            key={subindex}
                            as={NavLink}
                            to={subitem.path}>
                            {localize(subitem.label)}
                          </NavDropdown.Item>
                        ))}
                      </NavDropdown>
                    ) : (
                      <Nav.Link key={index} as={NavLink} to={item.path}>
                        {localize(item.label)}
                      </Nav.Link>
                    ))
                  );
                })}
            </Nav>
            {/* Organizations */}
            {user && user.organizations?.length > 0 && (
              <Nav align='end'>
                <NavDropdown
                  title={
                    selectedOrganization?.name || localize('organizations')
                  }
                  id={`nav-dropdown-organizations`}>
                  {user?.organizations.map((org) => (
                    <NavDropdown.Item
                      key={org.id}
                      active={org.id == selectedOrganization.id}
                      onClick={() => handleOrganizationChange(org.id)}>
                      {org.name}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              </Nav>
            )}
            {/* Lang */}
            <Nav align='end'>
              {navigationLinks?.lang.map((item, index) =>
                item.submenu ? (
                  <NavDropdown
                    key={index}
                    title={localize(item.label)}
                    id={`nav-dropdown-${index}`}>
                    {item.submenu.map((subitem, subindex) => (
                      <NavDropdown.Item
                        active={selectedLocale == subitem}
                        key={subindex}
                        onClick={() => setLocale(subitem)}>
                        {localize(`lang.${subitem}`)}
                      </NavDropdown.Item>
                    ))}
                  </NavDropdown>
                ) : (
                  <Nav.Link key={index} as={NavLink} to={item.path}>
                    {localize(item.label)}
                  </Nav.Link>
                )
              )}
            </Nav>
            {/* user dropdown */}
            {user && (
              <Dropdown align='end'>
                <Dropdown.Toggle
                  variant='link'
                  className='text-decoration-none d-flex align-items-center text-white'>
                  <span>{user.name}</span>
                </Dropdown.Toggle>

                <Dropdown.Menu key='menu-1'>
                  <Dropdown.Header>{user.email}</Dropdown.Header>
                  {navigationLinks?.user.map((item, index) =>
                    item.path ? (
                      <Dropdown.Item key={item.path} to={item.path}>
                        {localize(item.label)}
                      </Dropdown.Item>
                    ) : item.onclick ? (
                      <Dropdown.Item key={index} onClick={item.onclick}>
                        {localize(item.label)}
                      </Dropdown.Item>
                    ) : null
                  )}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </>
  );
};

export default NavigationBar;
